/**
 * Go Board Component
 *
 * Renders a Go board with grid lines (stones on intersections),
 * star points (hoshi), stone rendering with gradients,
 * hover preview, last move indicator, territory markers.
 */

import React, { useCallback, useState } from 'react';
import {
  GoBoard as GoBoardType,
  GoColor,
  GoPosition,
  GoBoardProps,
  GoBoardSize,
  STAR_POINTS,
} from '../types';
import './GoBoard.css';

const GoBoard: React.FC<GoBoardProps> = ({
  board,
  boardSize,
  validMoves,
  lastMove,
  currentPlayer,
  koPoint,
  territory,
  gameOver,
  onIntersectionClick,
  disabled = false,
}) => {
  const [hoverPos, setHoverPos] = useState<GoPosition | null>(null);

  const isValidMove = useCallback(
    (row: number, col: number): boolean => {
      return validMoves.some(m => m.row === row && m.col === col);
    },
    [validMoves]
  );

  const isLastMove = useCallback(
    (row: number, col: number): boolean => {
      return lastMove ? lastMove.row === row && lastMove.col === col : false;
    },
    [lastMove]
  );

  const isKoPoint = useCallback(
    (row: number, col: number): boolean => {
      return koPoint ? koPoint.row === row && koPoint.col === col : false;
    },
    [koPoint]
  );

  const isStarPoint = useCallback(
    (row: number, col: number): boolean => {
      const points = STAR_POINTS[boardSize];
      return points ? points.some(p => p.row === row && p.col === col) : false;
    },
    [boardSize]
  );

  const handleClick = useCallback(
    (row: number, col: number) => {
      if (disabled) return;
      if (!isValidMove(row, col)) return;
      onIntersectionClick({ row, col });
    },
    [disabled, isValidMove, onIntersectionClick]
  );

  const handleMouseEnter = useCallback(
    (row: number, col: number) => {
      if (disabled) return;
      if (isValidMove(row, col)) {
        setHoverPos({ row, col });
      }
    },
    [disabled, isValidMove]
  );

  const handleMouseLeave = useCallback(() => {
    setHoverPos(null);
  }, []);

  const getIntersectionAriaLabel = (row: number, col: number): string => {
    const colLetter = String.fromCharCode(65 + (col >= 8 ? col + 1 : col)); // Skip 'I'
    const rowNum = boardSize - row;
    const cell = board[row][col];

    if (cell) return `${colLetter}${rowNum}: ${cell} stone`;
    if (isValidMove(row, col)) return `${colLetter}${rowNum}: valid move for ${currentPlayer}`;
    return `${colLetter}${rowNum}: empty`;
  };

  const getTerritoryClass = (row: number, col: number): string => {
    if (!territory || !gameOver) return '';
    const t = territory[row][col];
    if (t === 'black') return 'go-board__territory--black';
    if (t === 'white') return 'go-board__territory--white';
    return '';
  };

  const sizeClass = boardSize <= 9 ? 'go-board--9' : boardSize <= 13 ? 'go-board--13' : 'go-board--19';

  return (
    <div
      className={`go-board ${sizeClass}`}
      role="grid"
      aria-label={`Go game board, ${boardSize} by ${boardSize}`}
    >
      <div className="go-board__grid">
        {/* SVG grid lines */}
        <svg
          className="go-board__lines"
          viewBox={`0 0 ${boardSize - 1} ${boardSize - 1}`}
          preserveAspectRatio="xMidYMid meet"
          aria-hidden="true"
        >
          {/* Horizontal lines */}
          {Array.from({ length: boardSize }, (_, i) => (
            <line
              key={`h-${i}`}
              x1={0}
              y1={i}
              x2={boardSize - 1}
              y2={i}
              className="go-board__line"
            />
          ))}
          {/* Vertical lines */}
          {Array.from({ length: boardSize }, (_, i) => (
            <line
              key={`v-${i}`}
              x1={i}
              y1={0}
              x2={i}
              y2={boardSize - 1}
              className="go-board__line"
            />
          ))}
          {/* Star points */}
          {(STAR_POINTS[boardSize] || []).map((p, i) => (
            <circle
              key={`star-${i}`}
              cx={p.col}
              cy={p.row}
              r={0.12}
              className="go-board__star-point"
            />
          ))}
        </svg>

        {/* Intersection buttons */}
        <div className="go-board__intersections">
          {Array.from({ length: boardSize }, (_, row) => (
            <div key={row} className="go-board__row" role="row">
              {Array.from({ length: boardSize }, (_, col) => {
                const cell = board[row][col];
                const valid = isValidMove(row, col);
                const last = isLastMove(row, col);
                const ko = isKoPoint(row, col);
                const isHovered = hoverPos && hoverPos.row === row && hoverPos.col === col;
                const territoryClass = getTerritoryClass(row, col);

                return (
                  <button
                    key={`${row}-${col}`}
                    className={[
                      'go-board__intersection',
                      valid && !disabled ? 'go-board__intersection--valid' : '',
                      last ? 'go-board__intersection--last-move' : '',
                      ko ? 'go-board__intersection--ko' : '',
                      territoryClass,
                    ].filter(Boolean).join(' ')}
                    onClick={() => handleClick(row, col)}
                    onMouseEnter={() => handleMouseEnter(row, col)}
                    onMouseLeave={handleMouseLeave}
                    disabled={disabled || !valid}
                    aria-label={getIntersectionAriaLabel(row, col)}
                    role="gridcell"
                    tabIndex={valid && !disabled ? 0 : -1}
                  >
                    {cell && (
                      <span
                        className={[
                          'go-board__stone',
                          `go-board__stone--${cell}`,
                          last ? 'go-board__stone--placed' : '',
                        ].filter(Boolean).join(' ')}
                        aria-hidden="true"
                      />
                    )}
                    {!cell && valid && !disabled && isHovered && (
                      <span
                        className={`go-board__hover-stone go-board__hover-stone--${currentPlayer}`}
                        aria-hidden="true"
                      />
                    )}
                    {!cell && gameOver && territoryClass && (
                      <span className="go-board__territory-marker" aria-hidden="true" />
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Column labels */}
      <div className="go-board__col-labels" aria-hidden="true">
        {Array.from({ length: boardSize }, (_, i) => {
          // Skip 'I' in Go notation
          const letter = String.fromCharCode(65 + (i >= 8 ? i + 1 : i));
          return (
            <span key={i} className="go-board__label">
              {letter}
            </span>
          );
        })}
      </div>

      {/* Row labels */}
      <div className="go-board__row-labels" aria-hidden="true">
        {Array.from({ length: boardSize }, (_, i) => (
          <span key={i} className="go-board__label">
            {boardSize - i}
          </span>
        ))}
      </div>
    </div>
  );
};

export { GoBoard };
export default GoBoard;
